import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, Truck, CheckCircle2, Circle } from "lucide-react";
import api from "@/api";
import { formatDateRange } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import { toast } from "sonner";

const STEPS = ["placed", "confirmed", "processing", "packed", "shipped", "out_for_delivery", "delivered"];
const LABELS = { placed: "Order Placed", confirmed: "Confirmed", processing: "Processing", packed: "Packed", shipped: "Shipped", out_for_delivery: "Out for Delivery", delivered: "Delivered" };

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [order, setOrder] = useState(params.get("order") || "");
  const [contact, setContact] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    if (busy || !order || !contact) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get("/track-order", { params: { order, contact } });
      setResult(data);
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };

  const currentIdx = result ? Math.max(...result.timeline.map((t) => STEPS.indexOf(t.status)).filter((i) => i >= 0), 0) : -1;

  return (
    <div className="tq-container py-12 sm:py-16 max-w-2xl" data-testid="track-order-page">
      <Seo title="Track Your Order" description="Track your TARISHQ FASHION order status and estimated delivery date." />
      <div className="text-center mb-10">
        <p className="tq-overline mb-2">Order Status</p>
        <h1 className="font-serif text-4xl sm:text-5xl">Track Your Order</h1>
      </div>
      <form onSubmit={track} className="tq-card p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input value={order} onChange={(e) => setOrder(e.target.value)} required placeholder="Order ID (e.g. TF100001)" data-testid="track-order-input" className="tq-input" />
        <input value={contact} onChange={(e) => setContact(e.target.value)} required placeholder="Email or mobile" data-testid="track-contact-input" className="tq-input" />
        <button type="submit" disabled={busy} data-testid="track-submit-btn" className="tq-btn-dark !px-5">{busy ? "…" : "Track"}</button>
      </form>
      {result && (
        <div className="mt-8 tq-card p-6 sm:p-8 tq-fade-up" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <p className="font-serif text-2xl">#{result.order_number}</p>
              <p className="text-xs text-[#C59B27]">Placed {new Date(result.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
            {result.delivery_estimate && (
              <p className="text-sm bg-[#FAFAFA] px-4 py-2 flex items-center gap-2">
                <Truck size={15} className="text-[#A8821B]" /> Est. delivery: <strong>{formatDateRange(result.delivery_estimate)}</strong>
              </p>
            )}
          </div>
          {result.status === "cancelled" ? (
            <p className="text-[#8B1E21] font-medium">This order has been cancelled.</p>
          ) : (
            <ol className="relative border-l-2 border-[#EAEAEA] ml-3 space-y-6">
              {STEPS.map((s, i) => {
                const done = i <= currentIdx;
                const entry = result.timeline.find((t) => t.status === s);
                return (
                  <li key={s} className="ml-6 relative" data-testid={`track-step-${s}`}>
                    <span className="absolute -left-[1.65rem] top-0.5">
                      {done ? <CheckCircle2 size={18} className="text-[#1A3C34] bg-white" /> : <Circle size={18} className="text-[#EAEAEA] bg-white" />}
                    </span>
                    <p className={`text-sm font-medium ${done ? "text-[#111111]" : "text-[#b9b2a6]"}`}>{LABELS[s]}</p>
                    {entry && <p className="text-xs text-[#8E8E93]">{new Date(entry.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>}
                  </li>
                );
              })}
            </ol>
          )}
          {result.awb && (
            <p className="mt-6 text-sm border-t border-[#EAEAEA] pt-4">
              <Package size={14} className="inline mr-2 text-[#A8821B]" />
              {result.courier && <span>{result.courier} · </span>}AWB: <strong>{result.awb}</strong>
              {result.tracking_url && <a href={result.tracking_url} target="_blank" rel="noopener noreferrer" className="ml-3 text-[#A8821B] underline">Track with courier</a>}
            </p>
          )}
          <ul className="mt-6 pt-4 border-t border-[#EAEAEA] space-y-2 text-sm">
            {result.items.map((it, i) => <li key={i} className="flex justify-between"><span>{it.name} · Size {it.size} × {it.qty}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
